// Generated by GitHub Copilot
// US1812367: Simplified Impact Form - removed deprecated fields
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import Select from "react-select";
import { FaClipboardList, FaCheck, FaRedo } from "react-icons/fa";
import { impactService } from "../../services/impactService";
import { workloadService } from "../../services/workloadService"; // US1794540: CI lookup
import AsyncWorkloadSelect from "./AsyncWorkloadSelect"; // US1794533: Application Name on Impact Report Form
import styles from "./ImpactForm.module.css";

// Generated by GitHub Copilot - US1812367: Simplified form with onResolve callback
function ImpactForm({
  onSubmit,
  onResolve,
  onReopen,
  initialValues,
  loading,
  problemNumber,
}) {
  const [formData, setFormData] = useState(
    initialValues || {
      applicationName: "",
      manualEntry: "", // Generated by GitHub Copilot - US1812308: Manual entry field
      ci: "", // Generated by GitHub Copilot - Configuration Item
      brand: "",
      status: "active", // US1812367: Default to "active" instead of "critical"
      impactDescription: "",
    },
  );

  // Generated by GitHub Copilot - Sync with initialValues when editing
  useEffect(() => {
    if (initialValues) {
      setFormData({
        ...initialValues,
      });
    }
  }, [initialValues]);

  const [errors, setErrors] = useState({});
  const [validated, setValidated] = useState(false);
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);

  // US1794540: CI lookup state
  // Generated by GitHub Copilot
  const [lookingUpCI, setLookingUpCI] = useState(false);
  const ciLookupTimer = useRef(null);
  const lastLookedUpCI = useRef("");

  // Generated by GitHub Copilot
  // US1803046: Duplicate detection state
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [duplicateImpact, setDuplicateImpact] = useState(null);
  const duplicateCheckTimer = useRef(null);
  const lastCheckedCI = useRef("");

  // Load brands from centralized brand API
  useEffect(() => {
    const loadBrands = async () => {
      try {
        const brandList = await impactService.getBrands();
        setBrands(brandList);
      } catch (err) {
        console.error("Failed to load brands:", err);
        // Fallback to empty array if loading fails
        setBrands([]);
      } finally {
        setLoadingBrands(false);
      }
    };

    loadBrands();
  }, []);

  // Convert brands array to select options
  const BRAND_OPTIONS = brands.map((brand) => ({
    label: brand,
    value: brand,
  }));

  // Convert brand string to select option
  const getBrandOption = () => {
    if (!formData.brand) return null;
    return BRAND_OPTIONS.find((opt) => opt.value === formData.brand);
  };

  // US1794540: Lookup workload by CI ID
  // Generated by GitHub Copilot
  const lookupByCIID = useCallback(async (ciID) => {
    // Don't lookup if empty or already looked up this value
    if (!ciID || ciID.trim() === "" || ciID === lastLookedUpCI.current) {
      return;
    }

    setLookingUpCI(true);
    lastLookedUpCI.current = ciID;

    try {
      const workload = await workloadService.lookupWorkloadByCIID(ciID);

      if (workload && workload.Name) {
        // Auto-populate application name
        setFormData((prev) => ({
          ...prev,
          applicationName: workload.Name,
        }));

        // Clear any application name errors
        setErrors((prev) => ({ ...prev, applicationName: "" }));
      } else {
        // CI not found - don't clear the application name field
        // User might have manually entered it
        console.debug(`No workload found for CI ID: ${ciID}`);
      }
    } catch (error) {
      console.error("Failed to lookup workload by CI ID:", error);
      // Don't show error to user - they can still submit manually
    } finally {
      setLookingUpCI(false);
    }
  }, []);

  // Generated by GitHub Copilot
  // US1803046: Check for duplicate impact by CI within this problem
  const checkForDuplicate = useCallback(
    async (ciID) => {
      // Skip if no problem number available
      if (!problemNumber) {
        console.debug("No problem number - skipping duplicate check");
        return;
      }

      // Don't check if empty or already checked this value
      if (!ciID || ciID.trim() === "" || ciID === lastCheckedCI.current) {
        return;
      }

      // Skip duplicate check in edit mode - we're editing an existing impact
      if (initialValues && initialValues.id) {
        console.debug("Edit mode - skipping duplicate check");
        return;
      }

      setCheckingDuplicate(true);
      lastCheckedCI.current = ciID;

      try {
        const existingImpact = await impactService.checkDuplicateByCI(
          problemNumber,
          ciID,
        );

        if (existingImpact) {
          // Duplicate found
          console.log("Duplicate impact found:", existingImpact);
          setDuplicateImpact(existingImpact);
        } else {
          // No duplicate
          console.debug("No duplicate impact found for CI:", ciID);
          setDuplicateImpact(null);
        }
      } catch (error) {
        console.error("Failed to check for duplicate impact:", error);
        // Don't block the user - they can still try to submit
        setDuplicateImpact(null);
      } finally {
        setCheckingDuplicate(false);
      }
    },
    [initialValues, problemNumber],
  );

  // Handle CI field changes with debounced lookup
  // Generated by GitHub Copilot
  const handleCIChange = (value) => {
    // Update the field immediately
    handleChange("ci", value);

    // Clear existing timers
    if (ciLookupTimer.current) {
      clearTimeout(ciLookupTimer.current);
    }
    if (duplicateCheckTimer.current) {
      clearTimeout(duplicateCheckTimer.current);
    }

    // Clear duplicate state if field is empty
    if (!value || value.trim() === "") {
      lastLookedUpCI.current = "";
      lastCheckedCI.current = "";
      setDuplicateImpact(null);
      return;
    }

    // Generated by GitHub Copilot
    // US1803046: Staggered debounce timers to reduce perceived latency
    // Duplicate check fires earlier (300ms) to provide faster feedback
    // Workload lookup fires later (500ms) as it's secondary information
    const trimmedValue = value.trim();

    duplicateCheckTimer.current = setTimeout(() => {
      checkForDuplicate(trimmedValue);
    }, 300);

    ciLookupTimer.current = setTimeout(() => {
      lookupByCIID(trimmedValue);
    }, 500);
  };

  // Handle CI field blur - immediate lookup
  // Generated by GitHub Copilot
  const handleCIBlur = () => {
    // Clear any pending timers
    if (ciLookupTimer.current) {
      clearTimeout(ciLookupTimer.current);
    }
    if (duplicateCheckTimer.current) {
      clearTimeout(duplicateCheckTimer.current);
    }

    // Perform immediate lookup and duplicate check if value exists
    if (formData.ci && formData.ci.trim() !== "") {
      const trimmedValue = formData.ci.trim();
      lookupByCIID(trimmedValue);
      // US1803046: Check for duplicates on blur
      checkForDuplicate(trimmedValue);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // US1867576: Accept EITHER applicationName (dropdown) OR manualEntry ("Other" field)
    const hasDropdownValue =
      formData.applicationName && formData.applicationName.trim() !== "";
    const hasManualValue =
      formData.manualEntry && formData.manualEntry.trim() !== "";

    if (!hasDropdownValue && !hasManualValue) {
      newErrors.applicationName =
        "Workload is required (select from dropdown or enter manually)";
    }

    if (!formData.brand || formData.brand === "") {
      newErrors.brand = "Brand is required";
    }

    if (
      !formData.impactDescription ||
      formData.impactDescription.trim().length < 10
    ) {
      newErrors.impactDescription =
        "Impact Description is required and must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidated(true);

    if (validateForm()) {
      // US1867576: Map manualEntry to applicationName for backend compatibility
      const submissionData = { ...formData };
      if (formData.manualEntry && formData.manualEntry.trim() !== "") {
        submissionData.applicationName = formData.manualEntry.trim();
      }
      // Remove manualEntry from submission (not needed by backend)
      delete submissionData.manualEntry;

      onSubmit(submissionData);
    }
  };

  // Generated by GitHub Copilot - US1812367: Handle resolve impact action
  const handleResolve = (e) => {
    e.preventDefault();
    setValidated(true);

    if (validateForm()) {
      // US1867576: Map manualEntry to applicationName for backend compatibility
      const submissionData = { ...formData, status: "resolved" };
      if (formData.manualEntry && formData.manualEntry.trim() !== "") {
        submissionData.applicationName = formData.manualEntry.trim();
      }
      // Remove manualEntry from submission (not needed by backend)
      delete submissionData.manualEntry;

      if (onResolve) {
        onResolve(submissionData);
      } else {
        // Fallback to onSubmit with resolved status
        onSubmit(submissionData);
      }
    }
  };

  // Generated by GitHub Copilot - Handle re-open impact action
  const handleReopen = (e) => {
    e.preventDefault();
    setValidated(true);

    if (validateForm()) {
      // US1867576: Map manualEntry to applicationName for backend compatibility
      const submissionData = { ...formData, status: "active" };
      if (formData.manualEntry && formData.manualEntry.trim() !== "") {
        submissionData.applicationName = formData.manualEntry.trim();
      }
      // Remove manualEntry from submission (not needed by backend)
      delete submissionData.manualEntry;

      if (onReopen) {
        onReopen(submissionData);
      } else {
        // Fallback to onSubmit with active status
        onSubmit(submissionData);
      }
    }
  };

  // Generated by GitHub Copilot - US1812367: Check if we're in edit mode
  const isEditMode = initialValues && initialValues.id;

  return (
    <div className={styles.formWrapper}>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        {/* Workload - US1794533: Searchable dropdown with workload search */}
        {/* Generated by GitHub Copilot - US1812367: Renamed from Application/Service Name to Workload */}
        <Form.Group className="mb-3">
          <Form.Label>Workload *</Form.Label>
          <AsyncWorkloadSelect
            value={formData.applicationName}
            onChange={(selected) => {
              // Generated by GitHub Copilot - US1812308: Clear manual entry when dropdown is used
              if (formData.manualEntry) {
                handleChange("manualEntry", "");
              }
              // Update both application name and CI when workload is selected
              handleChange("applicationName", selected.applicationName);
              if (selected.ci) {
                handleChange("ci", selected.ci);
                // Reset lookup tracker when dropdown is used
                lastLookedUpCI.current = selected.ci;
                // Generated by GitHub Copilot
                // US1803046: Reset duplicate check tracker and check for duplicates
                lastCheckedCI.current = "";
                checkForDuplicate(selected.ci);
              }
            }}
            isInvalid={!!errors.applicationName}
            disabled={loading || !!formData.manualEntry}
          />
          {errors.applicationName && (
            <div className="invalid-feedback d-block">
              {errors.applicationName}
            </div>
          )}
          <Form.Text className="text-muted">
            Search for a workload by typing its name
          </Form.Text>
        </Form.Group>

        {/* Generated by GitHub Copilot - US1812308: Manual Entry Field */}
        <Form.Group className="mb-3">
          <Form.Label>Other (Manual Entry)</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter application/service name manually"
            value={formData.manualEntry}
            onChange={(e) => {
              const newValue = e.target.value;
              // Generated by GitHub Copilot - US1812308: Clear dropdown when manual entry is used
              if (newValue && formData.applicationName) {
                handleChange("applicationName", "");
              }
              handleChange("manualEntry", newValue);
            }}
            disabled={loading || !!formData.applicationName}
            data-testid="impact-manual-entry-input"
          />
          <Form.Text className="text-muted">
            Use this field if the application/service is not found in the
            dropdown above
          </Form.Text>
        </Form.Group>

        {/* Generated by GitHub Copilot - CI (Configuration Item) */}
        {/* US1794540: Enhanced with CI lookup functionality */}
        <Form.Group className="mb-3">
          <Form.Label>
            CI (Configuration Item)
            {lookingUpCI && (
              <Spinner
                animation="border"
                size="sm"
                className="ms-2"
                variant="primary"
              />
            )}
            {/* Generated by GitHub Copilot - US1803046: Show duplicate checking indicator */}
            {checkingDuplicate && (
              <Spinner
                animation="border"
                size="sm"
                className="ms-2"
                variant="warning"
              />
            )}
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g., CI1234567"
            value={formData.ci}
            onChange={(e) => handleCIChange(e.target.value)}
            onBlur={handleCIBlur}
            readOnly={loading}
          />
          <Form.Text className="text-muted">
            Auto-populated when workload is selected, or enter CI to lookup
            workload name
          </Form.Text>

          {/* Generated by GitHub Copilot - US1803046: Duplicate warning alert */}
          {duplicateImpact && (
            <Alert variant="warning" className="mt-2 mb-0">
              <Alert.Heading className="h6">
                ⚠️ Duplicate Report Found
              </Alert.Heading>
              <p className="mb-0">
                There is already an impact reported for this workload in this
                problem. Please review the existing report before creating a new
                one.
              </p>
            </Alert>
          )}
        </Form.Group>

        {/* Brand */}
        <Form.Group className="mb-3">
          <Form.Label>Brand *</Form.Label>
          {/* Raw react-select — doesn't forward data-testid, wrap externally.
              classNamePrefix="rs" here (contrast PastIncidentsFilterMenu.js's
              "brandSelect" prefix), so the usual .locator(".rs__control") idiom applies. */}
          <div data-testid="impact-brand-select">
            <Select
              classNamePrefix="rs"
              value={getBrandOption()}
              onChange={(option) => handleChange("brand", option?.value || "")}
              options={BRAND_OPTIONS}
              placeholder={loadingBrands ? "Loading..." : "Select Brand"}
              isClearable
              isLoading={loadingBrands}
              isDisabled={loadingBrands}
              className={errors.brand ? "is-invalid" : ""}
            />
          </div>
          {errors.brand && (
            <div className="invalid-feedback d-block">{errors.brand}</div>
          )}
        </Form.Group>

        {/* Impact Description */}
        <Form.Group className="mb-3">
          <Form.Label>Impact Description *</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            placeholder="Describe the impact to users..."
            value={formData.impactDescription}
            onChange={(e) => handleChange("impactDescription", e.target.value)}
            isInvalid={!!errors.impactDescription}
            data-testid="impact-description-textarea"
          />
          <Form.Control.Feedback type="invalid">
            {errors.impactDescription}
          </Form.Control.Feedback>
          <Form.Text className="text-muted">
            Minimum 10 characters required
          </Form.Text>
        </Form.Group>

        {/* Generated by GitHub Copilot - US1812306: Form Actions */}
        {/* Generated by GitHub Copilot - US1812367: Added Resolve Impact button for edit mode */}
        <div className="d-flex justify-content-end mt-4 gap-2">
          {isEditMode && formData.status !== "resolved" && (
            <Button
              type="button"
              variant="success"
              disabled={loading || duplicateImpact !== null}
              className={`${styles.resolveButton} d-inline-flex align-items-center`}
              onClick={handleResolve}
              title="Mark this impact as resolved"
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Resolving...
                </>
              ) : (
                <>
                  <FaCheck className="me-2" />
                  Resolve
                </>
              )}
            </Button>
          )}
          {/* Generated by GitHub Copilot - Re-open button for resolved impacts */}
          {isEditMode && formData.status === "resolved" && (
            <Button
              type="button"
              variant="warning"
              disabled={loading}
              className={`${styles.reopenButton} d-inline-flex align-items-center`}
              onClick={handleReopen}
              title="Re-open this impact report"
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Re-opening...
                </>
              ) : (
                <>
                  <FaRedo className="me-2" />
                  Re-open
                </>
              )}
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={loading || duplicateImpact !== null}
            className={`${styles.submitButton} d-inline-flex align-items-center`}
            title={
              duplicateImpact
                ? "Cannot submit - a duplicate impact report already exists for this CI"
                : ""
            }
            data-testid="impact-submit-button"
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {initialValues ? "Saving..." : "Reporting..."}
              </>
            ) : (
              <>
                <FaClipboardList className="me-2" />
                {initialValues ? "Save" : "Report"}
              </>
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default ImpactForm;
